import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, Action } from '@ngrx/store';
import { catchError, map, Observable, of, switchMap, throttleTime } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { AuthService } from '@ninety/platform/services';

import { BaseEffects } from '../../../base.effects';
import { CHANGE_EMAIL_DIALOG, CONFIRM_EMAIL_DIALOG, VERIFY_PASSWORD_DIALOG } from '../../../tokens/auth-dialog.tokens';
import { SPAN_CONTROL } from '../../../tokens/span-control.token';
import { SegmentActions } from '../../analytics/segment/segment.actions';
import { selectCurrentPerson, selectCurrentPersonId } from '../../current-person/current-person.selectors';
import { NotificationActions } from '../../notifications/notification.actions';
import { SpinnerActions } from '../../spinner/spinner-state.actions';
import { LogOutActions } from '../logout/logout.actions';
import { AmplifyService } from '../services/amplify.service';
import { AmplifyError } from '../services/auth.types';

import { ChangeEmailActions } from './change-email.actions';

const apiV4 = 'api/v4';

@Injectable()
export class ChangeEmailEffects extends BaseEffects {
  action$ = inject(Actions);
  cognitoService = inject(AmplifyService);
  store = inject(Store);
  http = inject(HttpClient);
  authService = inject(AuthService);
  spanService = inject(SPAN_CONTROL);
  private readonly openVerifyPassword = inject(VERIFY_PASSWORD_DIALOG);
  private readonly openChangeEmail = inject(CHANGE_EMAIL_DIALOG);
  private readonly openConfirmEmail = inject(CONFIRM_EMAIL_DIALOG);

  openVerifyPasswordDialog$ = this.exhaustMapEffect({
    action: ChangeEmailActions.openVerifyPasswordDialog,
    exhaustMapFn: ({ invalidPassword }) => this.openVerifyPasswordDialog(invalidPassword),
  });

  submitVerifyPassword$ = this.switchMapEffect({
    action: ChangeEmailActions.submitVerifyPassword,
    switchMapFn: ({ password }) =>
      this.http.post(`${apiV4}/Cognito/VerifyPassword`, { password }).pipe(
        map(() => ChangeEmailActions.passwordVerified()),
        catchError((error: unknown) => of(ChangeEmailActions.passwordRejected({ error })))
      ),
  });

  passwordVerified$ = this.mapWithConcatEffect({
    action: ChangeEmailActions.passwordVerified,

    concatLatestFromFn: () => this.store.select(selectCurrentPerson),
    mapFn: (_, currentPerson) =>
      ChangeEmailActions.openChangeEmailDialog({ currentEmail: currentPerson?.primaryEmail ?? '' }),
  });

  passwordRejected$ = this.mapEffect({
    action: ChangeEmailActions.passwordRejected,
    mapFn: () => ChangeEmailActions.openVerifyPasswordDialog({ invalidPassword: true }),
  });

  openChangeEmailDialog$ = this.exhaustMapEffect({
    action: ChangeEmailActions.openChangeEmailDialog,
    exhaustMapFn: ({ currentEmail }) => this.openChangeEmailDialog(currentEmail),
  });

  updateEmail$ = this.switchMapEffect({
    action: ChangeEmailActions.updateCognito,
    switchMapFn: ({ newEmail }) => {
      const span = this.spanService.startSpan('settings.requestChangeEmail');
      // This will update the e-mail user attribute and send a confirmation code to the old e-mail
      return this.cognitoService.updateEmail(newEmail).pipe(
        map(() => {
          span.setAttributes({ cognitoUpdateSuccess: true });
          this.spanService.closeSpan(span);
          return ChangeEmailActions.updateCognitoSuccess({ newEmail });
        }),
        catchError((error: unknown) => {
          const err = error as { name?: string; message?: string };
          span.setAttributes({ 'error.name': err?.name, 'error.message': err?.message, cognitoUpdateSuccess: false });
          this.spanService.closeSpan(span);
          return of(ChangeEmailActions.updateCognitoFailure({ error: error as AmplifyError }));
        })
      );
    },
  });

  updateCognitoFailure_errorNotification$ = createEffect(() =>
    this.action$.pipe(
      ofType(ChangeEmailActions.updateCognitoFailure),
      map(({ error }) => {
        if (error?.name === 'AliasExistsException') {
          return NotificationActions.notify({
            message: 'This e-mail is in use on another account.  Please enter a different e-mail address.',
            duration: 20000,
          });
        }

        return NotificationActions.notify({
          message: 'We encountered an error updating your email address. Please contact support for assistance.',
          duration: 20000,
        });
      })
    )
  );

  updateCognitoFailure_reopenChangeEmailDialog$ = createEffect(() =>
    this.action$.pipe(
      ofType(ChangeEmailActions.updateCognitoFailure),
      concatLatestFrom(() => this.store.select(selectCurrentPerson)),
      map(([, currentPerson]) =>
        ChangeEmailActions.openChangeEmailDialog({ currentEmail: currentPerson?.primaryEmail ?? '' })
      )
    )
  );

  updateCognitoSuccess$ = this.mapEffect({
    action: ChangeEmailActions.updateCognitoSuccess,
    mapFn: ({ newEmail }) => ChangeEmailActions.confirmationCodeSent({ newEmail }),
  });

  updateEmailOpenConfirmationDialog$ = this.exhaustMapEffect({
    action: [ChangeEmailActions.confirmationCodeSent, ChangeEmailActions.reopenConfirmationCodeWithError],
    exhaustMapFn: ({ newEmail }) => this.openConfirmEmailDialog(newEmail),
  });

  updateEmailOpenConfirmationDialog_snackBar$ = this.mapEffect({
    action: ChangeEmailActions.confirmationCodeSent,
    mapFn: () => NotificationActions.showSnackbar({ message: 'Confirmation code sent to new email address!' }),
  });

  resendConfirmationCode$ = createEffect(() =>
    this.action$.pipe(
      ofType(ChangeEmailActions.resendConfirmationCode),
      throttleTime(5000), // ignore repeated clicks within 5 seconds
      switchMap(({ newEmail }) => {
        const span = this.spanService.startSpan('settings.requestChangeEmailNewCode');

        // sendVerificationCode sends to the current (old) address -- we have to restart the update flow for a new code.
        return this.cognitoService.updateEmail(newEmail).pipe(
          map(() => {
            this.spanService.closeSpan(span);
            return NotificationActions.showSnackbar({ message: 'Confirmation code sent to new email address!' });
          }),
          catchError((error: unknown) => {
            this.spanService.closeSpan(span);
            const message = (error as Error)?.message ?? 'An unknown error occurred trying to send the code.';
            return of(NotificationActions.notifyError({ error, message }));
          })
        );
      })
    )
  );

  updateEmailVerifyConfirmationCode$ = this.switchMapEffect({
    action: ChangeEmailActions.submitConfirmationCode,
    switchMapFn: ({ newEmail, code }) =>
      this.cognitoService.confirmEmailVerificationCode(code).pipe(
        map(() => ChangeEmailActions.confirmationCodeVerified({ newEmail })),
        catchError((error: unknown) => of(ChangeEmailActions.confirmationCodeFailed({ error, newEmail })))
      ),
  });

  confirmationCodeFailed_CheckError$ = createEffect(() =>
    this.action$.pipe(
      ofType(ChangeEmailActions.confirmationCodeFailed),
      map(({ error, newEmail }) => {
        const errorMessage = error instanceof Error ? error.message : '';
        let message: string;

        if (errorMessage.includes('Attempt limit exceeded')) {
          return NotificationActions.notifyError({ error, message: errorMessage });
        }

        if (errorMessage.includes('Invalid')) {
          message = 'Invalid verification code, please try again.';
        } else if (errorMessage.includes('Exceed')) {
          message = 'Too many attempts. Please resend the confirmation code and try again.';
        } else {
          message = 'An error occurred, please try again.';
        }

        return ChangeEmailActions.reopenConfirmationCodeWithError({ message, newEmail });
      })
    )
  );

  showErrorOnReopenConfirmationDialog$ = this.mapEffect({
    action: ChangeEmailActions.reopenConfirmationCodeWithError,
    mapFn: ({ message }) => NotificationActions.showError({ message, title: '' }),
  });

  confirmationCodeVerified_updatePersonEmail$ = this.mapEffect({
    action: ChangeEmailActions.confirmationCodeVerified,
    mapFn: ({ newEmail }) => ChangeEmailActions.updatePersonEmail({ newEmail }),
  });

  updatePersonEmail$ = createEffect(() =>
    this.action$.pipe(
      ofType(ChangeEmailActions.updatePersonEmail),
      concatLatestFrom(() => this.store.select(selectCurrentPersonId)),
      switchMap(([{ newEmail }, id]) => {
        const span = this.spanService.startSpan('settings.updatePersonEmail');
        return this.http.patch(`/api/v4/Person/${id}`, { primaryEmail: newEmail }).pipe(
          map(() => {
            this.spanService.closeSpan(span);
            return ChangeEmailActions.updatePersonEmailSuccess();
          }),
          catchError((e: unknown) => {
            this.spanService.closeSpan(span);
            return of(NotificationActions.notify({ message: e.toString() }));
          })
        );
      })
    )
  );

  analytics_updatePersonEmail$ = this.mapEffect({
    action: ChangeEmailActions.updatePersonEmail,
    mapFn: () => SegmentActions.track({ event: SegmentTrackEvent.SETTINGS_USER_EMAIL_CHANGED }),
  });

  updatePersonEmailSuccess_Snackbar$ = this.mapEffect({
    action: ChangeEmailActions.confirmationCodeVerified,
    mapFn: () =>
      NotificationActions.showSnackbar({
        message: 'New Primary Email updated! Please sign in with your new email.',
        duration: 20000,
      }),
  });

  updatePersonEmailSuccess_logout$ = this.tapEffect({
    action: ChangeEmailActions.confirmationCodeVerified,
    tapFn: () => this.store.dispatch(LogOutActions.initiateLogOut()),
  });

  startSpinner$ = createEffect(() =>
    this.action$.pipe(
      ofType(
        ChangeEmailActions.submitVerifyPassword,
        ChangeEmailActions.updateCognito,
        ChangeEmailActions.submitConfirmationCode
      ),
      map(() => SpinnerActions.startPrimary({ source: 'Change Email' }))
    )
  );

  stopSpinner$ = createEffect(() =>
    this.action$.pipe(
      ofType(
        ChangeEmailActions.cancelVerifyPassword,
        ChangeEmailActions.passwordVerified,
        ChangeEmailActions.passwordRejected,
        ChangeEmailActions.updateCognitoFailure,
        ChangeEmailActions.confirmationCodeSent,
        ChangeEmailActions.confirmationCodeFailed,
        ChangeEmailActions.confirmationCodeVerified,
        ChangeEmailActions.updatePersonEmailSuccess
      ),
      map(() => SpinnerActions.stopPrimary({ source: 'Change Email' }))
    )
  );

  private openVerifyPasswordDialog(invalidPassword: boolean): Observable<Action> {
    return this.openVerifyPassword({ data: { invalidPassword } })
      .afterClosed()
      .pipe(
        map(({ cancelled, password }) => {
          if (cancelled) return ChangeEmailActions.cancelVerifyPassword();
          return ChangeEmailActions.submitVerifyPassword({ password });
        })
      );
  }

  private openChangeEmailDialog(currentEmail: string): Observable<Action> {
    return this.openChangeEmail({
      data: { currentEmail },
    })
      .afterClosed()
      .pipe(
        map(({ newEmail, cancelled }) =>
          cancelled ? ChangeEmailActions.updateEmailCancelled() : ChangeEmailActions.updateCognito({ newEmail })
        )
      );
  }

  private openConfirmEmailDialog(newEmail: string): Observable<Action> {
    return this.openConfirmEmail({
      data: {
        destination: newEmail,
        resendAction: ChangeEmailActions.resendConfirmationCode({ newEmail }),
        workflow: 'CHANGE-EMAIL',
      },
      disableClose: true,
    })
      .afterClosed()
      .pipe(map(({ code }) => ChangeEmailActions.submitConfirmationCode({ newEmail, code })));
  }
}
